import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import 'leaflet/dist/leaflet.css'
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet'
import type { LatLngTuple } from 'leaflet'
import type { KeywordGridPoint } from '../../types'
import { CARTO_ATTRIBUTION, CARTO_TILE_URL } from '../../config'

/**
 * Local-visibility heatmap: renders the geo-grid of sample points used to
 * check a business's Google Maps ranking for one keyword (see
 * interfaces.KeywordRanking.GridPoints on the backend). Google Maps ranking
 * varies by the searcher's exact location, so a single-point check can be
 * misleading — this shows how consistently the business surfaces across its
 * local area for the given keyword.
 */

/** Rank tiers, matching the row-level badge tiers in SearchRankingsSection. */
function tierColor(point: KeywordGridPoint): string {
  if (!point.is_ranked || point.position === 0) return '#ef042d' // not ranked
  if (point.position <= 3) return '#00a63e' // top 3
  if (point.position <= 10) return '#1447e6' // top 10
  if (point.position <= 20) return '#d08700' // top 20 (first page)
  return '#f54900' // ranked but low (beyond first page)
}

interface LegendDotProps {
  color: string
  label: string
}

function LegendDot({ color, label }: LegendDotProps) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden />
      {label}
    </span>
  )
}

interface KeywordGridHeatmapProps {
  gridPoints: KeywordGridPoint[]
  averagePosition?: number
  coverage?: number
}

export function KeywordGridHeatmap({ gridPoints, averagePosition, coverage }: KeywordGridHeatmapProps) {
  const { t } = useTranslation('report')

  const center = useMemo<LatLngTuple | null>(() => {
    const c = gridPoints.find(p => p.is_center)
    if (c) return [c.latitude, c.longitude]
    if (gridPoints.length > 0) return [gridPoints[0].latitude, gridPoints[0].longitude]
    return null
  }, [gridPoints])

  if (!center || gridPoints.length === 0) return null

  const hasAverage = typeof averagePosition === 'number' && averagePosition > 0
  const hasCoverage = typeof coverage === 'number'

  return (
    <div className="bg-white border-2 border-[#d3d3d3] rounded-2xl p-4">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
        <h4 className="text-sm font-bold text-[#364153]">{t('rankings.grid.title')}</h4>
        <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-[#4a5565]">
          {hasAverage && (
            <span>{t('rankings.grid.avgPosition', { position: averagePosition!.toFixed(1) })}</span>
          )}
          {hasCoverage && (
            <span>{t('rankings.grid.coverage', { percent: Math.round(coverage! * 100) })}</span>
          )}
        </div>
      </div>
      <p className="text-xs text-[#4a5565] mb-3">{t('rankings.grid.subtitle')}</p>

      <div className="ranking-map-root relative z-0 h-[240px] sm:h-[280px] w-full overflow-hidden rounded-2xl border border-[#e5e7eb]">
        <MapContainer
          center={center}
          zoom={13}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={false}
        >
          <TileLayer
            url={CARTO_TILE_URL}
            attribution={CARTO_ATTRIBUTION}
          />
          {gridPoints.map((point, i) => (
            <CircleMarker
              key={i}
              center={[point.latitude, point.longitude]}
              radius={point.is_center ? 15 : 12}
              pathOptions={{
                color: '#ffffff',
                weight: point.is_center ? 3 : 2,
                fillColor: tierColor(point),
                fillOpacity: 0.9,
              }}
            >
              <Tooltip direction="top" offset={[0, -10]}>
                <span>
                  {point.is_ranked ? `#${point.position}` : t('rankings.unranked')}
                  {point.is_center ? ` · ${t('rankings.grid.yourLocation')}` : ''}
                </span>
              </Tooltip>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-[#4a5565]">
        <LegendDot color="#00a63e" label={t('rankings.grid.legend.top3')} />
        <LegendDot color="#1447e6" label={t('rankings.grid.legend.top10')} />
        <LegendDot color="#d08700" label={t('rankings.grid.legend.top20')} />
        <LegendDot color="#f54900" label={t('rankings.grid.legend.low')} />
        <LegendDot color="#ef042d" label={t('rankings.unranked')} />
      </div>
    </div>
  )
}
